/**
 * 日志模块
 * 提供分级日志、环境感知、自动移除生产环境调试日志
 */
import { isAppError } from '@/errors/index.js'

// 日志级别
export const LOG_LEVEL = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
}

// 当前日志级别
let currentLevel = LOG_LEVEL.DEBUG

// 是否为生产环境
const isProduction = import.meta.env.PROD

/**
 * 设置日志级别
 * @param {number} level
 */
export function setLogLevel(level) {
  currentLevel = level
}

/**
 * 获取日志前缀
 */
function getPrefix(module, level) {
  const time = new Date().toLocaleTimeString('zh-CN', { hour12: false })
  const levelTag = {
    [LOG_LEVEL.DEBUG]: '🔍',
    [LOG_LEVEL.INFO]: 'ℹ️',
    [LOG_LEVEL.WARN]: '⚠️',
    [LOG_LEVEL.ERROR]: '❌'
  }[level] || '📝'

  return `[${time}] ${levelTag} [${module}]`
}

/**
 * 格式化错误对象
 */
function formatError(error) {
  if (!error) return ''

  if (isAppError(error)) {
    return `[${error.code}] ${error.message}`
  }

  if (error instanceof Error) {
    return `${error.message}${error.stack ? '\n' + error.stack : ''}`
  }

  return String(error)
}

/**
 * Debug 日志
 */
export function debug(module, ...args) {
  if (currentLevel > LOG_LEVEL.DEBUG) return
  console.debug(getPrefix(module, LOG_LEVEL.DEBUG), ...args)
}

/**
 * Info 日志
 */
export function info(module, ...args) {
  if (currentLevel > LOG_LEVEL.INFO) return
  console.info(getPrefix(module, LOG_LEVEL.INFO), ...args)
}

/**
 * 警告日志
 */
export function warn(module, ...args) {
  if (currentLevel > LOG_LEVEL.WARN) return
  console.warn(getPrefix(module, LOG_LEVEL.WARN), ...args)
}

/**
 * 错误日志
 */
export function error(module, ...args) {
  if (currentLevel > LOG_LEVEL.ERROR) return
  const formattedArgs = args.map(arg => {
    if (isAppError(arg) || arg instanceof Error) {
      return formatError(arg)
    }
    return arg
  })
  console.error(getPrefix(module, LOG_LEVEL.ERROR), ...formattedArgs)
}

/**
 * 格式化输出（返回字符串）
 */
export function format(module, level, ...args) {
  const prefix = getPrefix(module, level)
  const content = args.map(arg => {
    if (isAppError(arg)) return formatError(arg)
    if (arg instanceof Error) return formatError(arg)
    if (typeof arg === 'object') {
      try {
        return JSON.stringify(arg)
      } catch {
        return String(arg)
      }
    }
    return String(arg)
  }).join(' ')
  return `${prefix} ${content}`
}

// 日志模块预设
export const logger = {
  module: (name) => ({
    debug: (...args) => debug(name, ...args),
    info: (...args) => info(name, ...args),
    warn: (...args) => warn(name, ...args),
    error: (...args) => error(name, ...args),
    format: (level, ...args) => format(name, level, ...args)
  })
}

// 开发环境下默认开启 DEBUG，生产环境默认 WARN
if (isProduction) {
  currentLevel = LOG_LEVEL.WARN
}

export default {
  LOG_LEVEL,
  setLogLevel,
  debug,
  info,
  warn,
  error,
  logger
}
